/**
 * Settings > Project > MCP — API keys for Trodo's hosted MCP server.
 *
 * Deliberately keys-only. This page used to be a 900-line Integrations detail
 * view that inlined per-client setup walkthroughs (Claude Code HTTP/stdio,
 * Cursor JSON/Web/Command, OAuth). Those drifted from the real CLI flags and
 * had to be maintained in two places, so they now live in the docs — see
 * docs/client/content/docs/mcp.mdx — and this page just mints and revokes keys.
 * Add setup prose to the docs, not here.
 *
 * Gating mirrors the server:
 *   - listing needs team membership only (never returns a secret, just a prefix)
 *   - create/revoke need INTEGRATIONS_DEVELOPER_MANAGE (backend/routes/mcpKeysRouter.js)
 *   - the MCP server itself rejects free-plan keys with HTTP 402, so there's no
 *     point minting one — the create control is hidden until the team is paid.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, Trash2 } from 'lucide-react';

import axiosInstance from 'shared/services/api/axiosInstance';
import { useTeam } from 'shared/context/teamContext';
import { useSubscription, isPremiumPlanType } from 'shared/context/subscriptionContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { usePermissions } from 'shared/hooks/usePermissions';
import { PERMISSIONS } from 'shared/constants/permissions';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import {
  settingsInputClass,
  settingsSolidButtonClass,
  settingsIconButtonClass,
  settingsTableShellClass,
  settingsModalClass,
} from 'features/settings/components/settingsTheme';
import { BrandLoaderInline } from 'features/settings/components/settingsLoader';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
  activityNeutralButtonClass,
} from 'features/activity/styles/dataTableStyles';

const DOCS_URL = 'https://docs.trodo.ai/mcp';
const SERVER_URL = 'https://mcp.trodo.ai/mcp';

const RATE_LIMIT_OPTIONS = [60, 600, 1500, 6000].map((n) => ({ value: n, label: `${n} / min` }));

function humanError(e) {
  return e?.response?.data?.error_description || e?.response?.data?.error || e?.message || 'Request failed';
}

/** Small neutral icon button that copies `text`. */
function CopyButton({ text, ariaLabel }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={ariaLabel || 'Copy'}
      title={copied ? 'Copied' : 'Copy'}
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); } catch { /* clipboard blocked: nothing useful to say */ }
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }}
      className={settingsIconButtonClass}
    >
      {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Create-key modal
// ---------------------------------------------------------------------------

function CreateKeyModal({ teamId, onClose, onCreated }) {
  const [label, setLabel] = useState('');
  // Catalog comes from the server (GET /mcp/keys/scopes) so this form can't
  // drift from backend/config/mcpScopes.js — which is what left the old
  // Integrations page offering 4 of the 6 grantable scopes.
  const [catalog, setCatalog] = useState(null); // null = loading
  const [scopes, setScopes] = useState({});
  const [rateLimit, setRateLimit] = useState(600);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/mcp/keys/scopes');
        if (!alive) return;
        const list = Array.isArray(data?.scopes) ? data.scopes : [];
        setCatalog(list);
        // Honour the server's own defaults; PII is is_default:false there.
        setScopes(Object.fromEntries(list.map((s) => [s.id, true])));
      } catch (e) {
        if (!alive) return;
        setCatalog([]);
        setError(humanError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  const enabledScopes = useMemo(
    () => (catalog || []).map((s) => s.id).filter((id) => scopes[id]),
    [catalog, scopes],
  );

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      const { data } = await axiosInstance.post(
        '/mcp/keys',
        { label: label.trim(), allowed_scopes: enabledScopes, rate_limit_per_min: rateLimit },
        { params: { teamId } },
      );
      onCreated(data);
    } catch (er) {
      setError(humanError(er));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6"
      style={{ background: 'var(--surface-overlay)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mcp-create-key-title"
        className={`${settingsModalClass} max-w-md p-5`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="mcp-create-key-title" className="mb-3 text-[14px] font-semibold text-[color:var(--text-primary)]">
          Create key
        </h2>

        <label htmlFor="mcp-key-label" className="mb-1 block text-[12px] text-[color:var(--text-muted)]">
          Label
        </label>
        <input
          id="mcp-key-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Cursor on Alice's laptop"
          required
          className={settingsInputClass}
        />

        {/* No scope picker: the key carries every tool and the MCP client
            turns individual tools off. */}
        <div className="mt-4">
          <div className="mb-1 text-[12px] text-[color:var(--text-muted)]">
            Rate limit
          </div>
          <div className="w-40">
            <BrandDropdown
              value={rateLimit}
              onChange={setRateLimit}
              options={RATE_LIMIT_OPTIONS}
              ariaLabel="Rate limit per minute"
            />
          </div>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12px] text-[color:var(--feedback-error)]">{error}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={activityNeutralButtonClass}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={creating || !label.trim() || enabledScopes.length === 0}
            className={settingsSolidButtonClass}
          >
            {creating ? 'Creating…' : 'Create key'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function McpPanel() {
  const { selectedTeam } = useTeam();
  const subscription = useSubscription();
  const dialog = useAppDialog();
  const { can } = usePermissions();

  const teamId = selectedTeam?.id || selectedTeam?._id || null;
  const teamName = selectedTeam?.name;
  const canManage = can(PERMISSIONS.INTEGRATIONS_DEVELOPER_MANAGE);

  // Same gate the page carried under Integrations: a paid plan, allowing
  // trialing teams through (the context's own isActive excludes trialing).
  const status = (subscription?.status || '').toLowerCase();
  const isPaid = isPremiumPlanType(subscription?.plan)
    && (status === 'active' || status === 'trialing' || !!subscription?.isActive);

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [justCreated, setJustCreated] = useState(null);

  const refresh = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/mcp/keys', { params: { teamId } });
      setKeys(Array.isArray(data?.keys) ? data.keys : []);
      setError(null);
    } catch (e) {
      setError(humanError(e));
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { if (isPaid) refresh(); }, [isPaid, refresh]);

  async function revoke(key) {
    const ok = await dialog.confirm(
      `Anything still using "${key.label}" will stop working immediately. This cannot be undone.`,
      { title: 'Revoke this key?', confirmLabel: 'Revoke', cancelLabel: 'Cancel', danger: true },
    );
    if (!ok) return;
    try {
      await axiosInstance.delete(`/mcp/keys/${key.id}`, { params: { teamId } });
      await refresh();
    } catch (e) {
      setError(humanError(e));
    }
  }

  const billingHref = teamName ? `/${encodeURIComponent(teamName)}/settings/billing` : '/settings/billing';
  const colSpan = canManage ? 5 : 4;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1 text-[12px] text-[color:var(--text-muted)]">
          <span>Server</span>
          <code className="truncate font-mono text-[12px] text-[color:var(--text-primary)]">{SERVER_URL}</code>
          <CopyButton text={SERVER_URL} ariaLabel="Copy MCP server URL" />
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1 text-[color:var(--text-muted)] underline-offset-2 hover:text-[color:var(--text-primary)] hover:underline"
          >
            Docs
          </a>
        </div>
        {isPaid && canManage && !showCreate && !justCreated && (
          <button type="button" onClick={() => setShowCreate(true)} className={settingsSolidButtonClass}>
            Create key
          </button>
        )}
      </div>

      {!isPaid && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12px] text-[color:var(--text-muted)]">Growth or Enterprise required.</p>
          <a href={billingHref} className={settingsSolidButtonClass}>Upgrade</a>
        </div>
      )}

      {!canManage && (
        <p className="text-[12px] text-[color:var(--text-muted)]">Only owners, admins and developers can create or revoke keys.</p>
      )}

      {error && <p role="alert" className="text-[12px] text-[color:var(--feedback-error)]">{error}</p>}

      {/* Shown once, right after creation: the secret is never retrievable again */}
      {justCreated && (
        <div className={`${settingsTableShellClass} p-3`}>
          <p className="text-[12px] font-semibold text-[color:var(--text-primary)]">
            Copy this key now. It won't be shown again.
          </p>
          <div className="mt-2 flex items-center gap-1">
            <code className="min-w-0 flex-1 break-all font-mono text-[12px] text-[color:var(--text-primary)]">
              {justCreated.key}
            </code>
            <CopyButton text={justCreated.key} ariaLabel="Copy the new API key" />
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => { setJustCreated(null); refresh(); }}
              className={activityNeutralButtonClass}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {isPaid && loading && keys.length === 0 ? (
        <BrandLoaderInline label="Loading keys" />
      ) : (
      <div className={settingsTableShellClass}>
        <div className="overflow-x-auto">
        <table className="w-full text-left">
          <caption className="sr-only">MCP API keys</caption>
          <thead>
            <tr className={tableHeaderRowClass}>
              <th scope="col" className={tableHeaderCellClass}>Label</th>
              <th scope="col" className={tableHeaderCellClass}>Key</th>
              <th scope="col" className={tableHeaderCellClass}>Rate</th>
              <th scope="col" className={tableHeaderCellClass}>Last used</th>
              {canManage && (
                <th scope="col" className="h-8 w-1 whitespace-nowrap px-3"><span className="sr-only">Actions</span></th>
              )}
            </tr>
          </thead>
          <tbody>
            {!isPaid && (
              <tr>
                <td colSpan={colSpan} className="px-3.5 py-8 text-center text-[12px] text-[color:var(--text-muted)]">
                  No keys
                </td>
              </tr>
            )}
            {isPaid && !loading && keys.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="px-3.5 py-8 text-center text-[12px] text-[color:var(--text-muted)]">
                  No keys yet
                </td>
              </tr>
            )}
            {isPaid && keys.map((k) => (
              // tableRowClass carries cursor-pointer for drill-down tables;
              // `!` forces the default cursor on rows that open nothing.
              <tr key={k.id} className={`${tableRowClass} group !cursor-default`}>
                <td className={tableDataCellClass}>{k.label}</td>
                <td className={`${tableDataCellClass} font-mono text-[11px] text-[color:var(--text-muted)]`}>
                  {k.key_prefix ? `${k.key_prefix}…` : '—'}
                </td>
                <td className={`${tableDataCellClass} whitespace-nowrap text-[color:var(--text-muted)]`}>
                  {k.rate_limit_per_min}/min
                </td>
                <td className={`${tableDataCellClass} whitespace-nowrap text-[color:var(--text-muted)]`}>
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'Never'}
                </td>
                {canManage && (
                  <td className="w-1 whitespace-nowrap px-3 text-right">
                    <button
                      type="button"
                      onClick={() => revoke(k)}
                      aria-label={`Revoke ${k.label}`}
                      title="Revoke"
                      className={`${settingsIconButtonClass} opacity-0 group-hover:opacity-100 focus-visible:opacity-100`}
                    >
                      <Trash2 size={13} aria-hidden />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      )}

      {showCreate && (
        <CreateKeyModal
          teamId={teamId}
          onClose={() => setShowCreate(false)}
          onCreated={(data) => { setJustCreated(data); setShowCreate(false); }}
        />
      )}
    </div>
  );
}
